const HIDE_AFTER_DOWNWARD_PX = 220;

class KryozonProductAnchorNav extends HTMLElement {
  #abortController = new AbortController();
  #items = [];
  #lastScrollY = window.scrollY;
  #downwardDistance = 0;
  #alignmentRaf = null;
  #alignmentTimers = [];
  #targetResizeObserver = null;
  #rafId = null;
  #resizeObserver = null;
  #visibleLockUntil = 0;
  #wrapper = null;

  connectedCallback() {
    this.#wrapper = this.closest('.shopify-section') || this.parentElement;
    this.#items = [...this.querySelectorAll('[data-anchor-link]')]
      .map((link) => {
        const selector = link.dataset.target;
        const target = selector ? document.querySelector(selector) : null;
        const item = link.closest('.kz-product-anchor-nav__item');

        if (!target) {
          if (item) item.hidden = true;
          return null;
        }

        return { link, target };
      })
      .filter(Boolean);

    if (!this.#items.length || !this.#wrapper) {
      if (this.#wrapper) this.#wrapper.hidden = true;
      return;
    }

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    window.addEventListener('resize', this.#handleResize, { passive: true, signal });
    window.addEventListener('wheel', this.#cancelTargetAlignment, { passive: true, signal });
    window.addEventListener('touchstart', this.#cancelTargetAlignment, { passive: true, signal });
    window.addEventListener('keydown', this.#cancelTargetAlignment, { signal });
    document.addEventListener('shopify:section:load', this.#handleResize, { signal });

    this.#resizeObserver = new ResizeObserver(this.#syncHeaderOffset);
    const header = document.querySelector('#header-component');
    if (header) this.#resizeObserver.observe(header);
    this.#resizeObserver.observe(this);

    this.#wrapper.dataset.anchorVisibility = 'visible';
    this.#syncHeaderOffset();
    this.#updateState();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#cancelTargetAlignment();
    this.#resizeObserver?.disconnect();
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
  }

  #handleScroll = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateState();
    });
  };

  #handleResize = () => {
    this.#syncHeaderOffset();
    this.#updateState();
  };

  #syncHeaderOffset = () => {
    if (!this.#wrapper) return;
    const header = document.querySelector('#header-component[data-sticky-state="active"]');
    const height = header ? Math.round(header.getBoundingClientRect().height) : 0;
    this.#wrapper.style.setProperty('--kz-product-anchor-top', `${height}px`);
  };

  #updateState() {
    if (!this.#wrapper) return;

    this.#syncHeaderOffset();
    const scrollY = window.scrollY;
    const delta = scrollY - this.#lastScrollY;
    const previous = this.#wrapper.previousElementSibling;
    const naturalTop = previous
      ? previous.getBoundingClientRect().bottom + scrollY
      : this.#wrapper.offsetTop;
    const headerOffset = Number.parseFloat(
      getComputedStyle(this.#wrapper).getPropertyValue('--kz-product-anchor-top')
    ) || 0;
    const isStuck = scrollY + headerOffset >= naturalTop - 1;

    this.#wrapper.dataset.anchorStuck = String(isStuck);
    if (!isStuck || scrollY < 8 || performance.now() < this.#visibleLockUntil) {
      this.#downwardDistance = 0;
      this.#wrapper.dataset.anchorVisibility = 'visible';
    } else if (delta < -6) {
      this.#downwardDistance = 0;
      this.#wrapper.dataset.anchorVisibility = 'visible';
    } else if (delta > 0) {
      this.#downwardDistance += delta;
      if (this.#downwardDistance >= HIDE_AFTER_DOWNWARD_PX) {
        this.#wrapper.dataset.anchorVisibility = 'hidden';
      }
    }

    this.#wrapper.dataset.anchorDownwardDistance = String(Math.round(this.#downwardDistance));
    this.#updateActiveLink();
    this.#lastScrollY = scrollY;
  }

  #updateActiveLink() {
    const headerOffset = this.#wrapper
      ? Number.parseFloat(getComputedStyle(this.#wrapper).getPropertyValue('--kz-product-anchor-top')) || 0
      : 0;
    const probe = headerOffset + this.getBoundingClientRect().height + 18;
    let active = this.#items[0];

    for (const item of this.#items) {
      if (item.target.getBoundingClientRect().top <= probe) active = item;
    }

    for (const item of this.#items) {
      const isActive = item === active;
      if (isActive) {
        item.link.setAttribute('aria-current', 'location');
      } else {
        item.link.removeAttribute('aria-current');
      }
    }
  }

  #cancelTargetAlignment = (event) => {
    this.#alignmentTimers.forEach((timer) => clearTimeout(timer));
    this.#alignmentTimers = [];
    this.#targetResizeObserver?.disconnect();
    this.#targetResizeObserver = null;
    if (this.#alignmentRaf !== null) cancelAnimationFrame(this.#alignmentRaf);
    this.#alignmentRaf = null;
    if (event?.isTrusted) this.#visibleLockUntil = performance.now();
  };

  #alignTarget(item, includeHeader, behavior = 'auto') {
    const targetTop = item.target.getBoundingClientRect().top + window.scrollY;
    const header = document.querySelector('#header-component');
    const headerOffset = includeHeader && header ? header.getBoundingClientRect().height : 0;
    const navOffset = this.getBoundingClientRect().height;
    const top = Math.max(0, targetTop - headerOffset - navOffset - 12);

    if (Math.abs(window.scrollY - top) > 2) window.scrollTo({ top, behavior });
  }

  #followTarget(item, includeHeader, reducedMotion) {
    this.#cancelTargetAlignment();
    const shiftingContent = document.querySelector('.kz-specs__rich');
    for (const image of shiftingContent?.querySelectorAll('img') || []) {
      image.loading = 'eager';
      image.fetchPriority = 'high';
    }
    this.#alignTarget(item, includeHeader, reducedMotion ? 'auto' : 'smooth');

    const realign = () => {
      if (this.#alignmentRaf !== null) return;
      this.#alignmentRaf = requestAnimationFrame(() => {
        this.#alignmentRaf = null;
        this.#alignTarget(item, includeHeader);
      });
    };

    for (const delay of [350, 800, 1400, 2200, 3400, 4800, 7000, 10000, 14000]) {
      this.#alignmentTimers.push(setTimeout(realign, delay));
    }

    const observerTimer = setTimeout(() => {
      if (!shiftingContent) return;
      this.#targetResizeObserver = new ResizeObserver(realign);
      this.#targetResizeObserver.observe(shiftingContent);
    }, 250);
    this.#alignmentTimers.push(observerTimer);
    this.#alignmentTimers.push(setTimeout(this.#cancelTargetAlignment, 15000));
  }

  #handleClick = (event) => {
    const link = event.target.closest('[data-anchor-link]');
    if (!link || !this.contains(link)) return;
    const item = this.#items.find((candidate) => candidate.link === link);
    if (!item) return;

    event.preventDefault();
    this.#downwardDistance = 0;
    this.#visibleLockUntil = performance.now() + 15000;
    if (this.#wrapper) this.#wrapper.dataset.anchorVisibility = 'visible';

    const targetTop = item.target.getBoundingClientRect().top + window.scrollY;
    const isUpwardJump = targetTop < window.scrollY;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#followTarget(item, isUpwardJump, reducedMotion);
    history.replaceState(null, '', `${location.pathname}${location.search}${link.getAttribute('href')}`);
    link.setAttribute('aria-current', 'location');
    link.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
  };
}

if (!customElements.get('kryozon-product-anchor-nav')) {
  customElements.define('kryozon-product-anchor-nav', KryozonProductAnchorNav);
}
